import { type ReactNode, useId } from "react";
import { Button, Dialog, Modal, ModalOverlay } from "@its-engineering-and-development/ui-foundation/components";
import { Spinner } from "@app/components/base/spinner/spinner";

type ConfirmDialogTone = "primary" | "primary-destructive" | "secondary";

const MAX_WIDTH_CLASS = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg" } as const;
const TITLE_SIZE_CLASS = { base: "text-base", lg: "text-lg" } as const;

export interface ConfirmDialogProps {
    /** Whether the dialog is visible. */
    isOpen: boolean;
    /** Called when the dialog requests to open/close (Cancel, overlay dismiss). */
    onOpenChange: (open: boolean) => void;
    /** Heading text. */
    title: string;
    /** Body content (plain text or nodes). */
    children: ReactNode;
    /** Confirm button label when idle. */
    confirmLabel: string;
    /** Cancel button label. Defaults to "Cancel". */
    cancelLabel?: string;
    /** Confirm button label shown next to the spinner while `isConfirming`. */
    busyLabel?: string;
    /** Confirm button color. Defaults to "primary-destructive". */
    confirmTone?: ConfirmDialogTone;
    /** Dialog max width. Defaults to "md". */
    maxWidth?: keyof typeof MAX_WIDTH_CLASS;
    /** Title font size. Defaults to "lg". */
    titleSize?: keyof typeof TITLE_SIZE_CLASS;
    /** When true, buttons are disabled and the confirm button shows a spinner. */
    isConfirming?: boolean;
    /** Keeps the confirm button disabled (e.g. until a confirmation phrase is typed). */
    isConfirmDisabled?: boolean;
    /** Confirm handler. May be async. */
    onConfirm: () => void | Promise<void>;
}

/**
 * Reusable confirmation dialog built on the ui-foundation modal primitives.
 *
 * Centralizes the ModalOverlay → Modal → Dialog scaffolding, title/body layout,
 * Cancel/Confirm footer, and the busy (spinner) state so feature pages don't
 * re-implement the same markup for every confirmation prompt.
 */
export function ConfirmDialog({
    isOpen,
    onOpenChange,
    title,
    children,
    confirmLabel,
    cancelLabel = "Cancel",
    busyLabel,
    confirmTone = "primary-destructive",
    maxWidth = "md",
    titleSize = "lg",
    isConfirming = false,
    isConfirmDisabled = false,
    onConfirm,
}: ConfirmDialogProps) {
    const titleId = useId();

    return (
        <ModalOverlay
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open && !isConfirming) onOpenChange(false);
            }}
            isDismissable={!isConfirming}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
        >
            <Modal className={MAX_WIDTH_CLASS[maxWidth]}>
                <Dialog aria-labelledby={titleId} className="outline-none">
                    <div className="w-full rounded-xl bg-white p-6 shadow-xl">
                        <h2 id={titleId} className={`${TITLE_SIZE_CLASS[titleSize]} font-semibold text-gray-900`}>
                            {title}
                        </h2>
                        <div className="mt-2 text-sm text-gray-600">{children}</div>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button color="secondary" size="md" isDisabled={isConfirming} onPress={() => onOpenChange(false)}>
                                {cancelLabel}
                            </Button>
                            <Button color={confirmTone} size="md" isDisabled={isConfirming || isConfirmDisabled} onPress={() => void onConfirm()}>
                                {isConfirming ? (
                                    <span className="flex items-center gap-2">
                                        <Spinner className="h-4 w-4" />
                                        {busyLabel ?? confirmLabel}
                                    </span>
                                ) : (
                                    confirmLabel
                                )}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
